import { useEffect } from "react";

export const SITE_NAME = "Horizon Brief";
export const SITE_URL = "https://horizonbrief.it";
export const TAGLINE = {
  it: "Tutte le novità di scienza, tecnologia ed economia in un unico sito",
  en: "All the latest in science, technology and economics in one place",
};
export const SITE_DESCRIPTION = {
  it: "Horizon Brief è un'agenzia di stampa automatizzata che riscrive ogni giorno notizie da università, istituti di ricerca, banche centrali e aziende tech: scienza, biologia e medicina, tecnologia, microprocessori, teorie economiche e storia economica.",
  en: "Horizon Brief is an automated news agency rewriting daily news from universities, research institutes, central banks and tech companies: science, biology and medicine, technology, semiconductors, economic theory and economic history.",
};
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

const setMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href, hreflang) => {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const useSeo = ({ title, description, path = "/", image, type = "website", lang = "it", jsonLd }) => {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — ${TAGLINE[lang] || TAGLINE.it}`;
    const desc = (description || SITE_DESCRIPTION[lang] || SITE_DESCRIPTION.it).slice(0, 300);
    const url = `${SITE_URL}/${lang}${path}`;
    const img = image || DEFAULT_IMAGE;
    document.title = fullTitle;
    document.documentElement.lang = lang;
    setMeta("name", "description", desc);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", type);
    setMeta("property", "og:image", img);
    setMeta("property", "og:locale", lang === "it" ? "it_IT" : "en_US");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", img);
    setLink("canonical", url);
    setLink("alternate", `${SITE_URL}/it${path}`, "it");
    setLink("alternate", `${SITE_URL}/en${path}`, "en");
    setLink("alternate", `${SITE_URL}/it${path}`, "x-default");

    let script = document.getElementById("seo-jsonld");
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.id = "seo-jsonld";
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
  }, [title, description, path, image, type, lang, jsonLd]);
};

export const articleJsonLd = (article, lang) => {
  const title = lang === "it" && article.title_it ? article.title_it : article.title_en;
  const summary = lang === "it" && article.summary_it ? article.summary_it : article.summary_en;
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: (title || "").slice(0, 110),
    description: (summary || "").slice(0, 300),
    image: [article.image_url || DEFAULT_IMAGE],
    datePublished: article.published_at,
    dateModified: article.updated_at || article.published_at,
    inLanguage: lang,
    articleSection: article.category,
    keywords: (article.tags || []).join(", "),
    author: { "@type": "Organization", name: `${SITE_NAME} Editorial`, url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: DEFAULT_IMAGE },
    },
    mainEntityOfPage: `${SITE_URL}/${lang}/article/${article.id}`,
    isBasedOn: article.original_url,
  };
};
